import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled, { css, useTheme } from 'styled-components';

import {
  FloatingFilterButton as BaseFloatingFilterButton,
  useBreakpoint,
  FilterDrawerButton as BaseFilterDrawerButton,
} from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import BaseFilterIcon from '../FilterIcon';

export const productGridFilterDrawerBtnCountStyle = css`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.default};
  letter-spacing: ${0.3 / 14}em;
`;

const productGridFilterDrawerBtnLabelStyleVariant = css`
  color: ${({ theme }) => theme.colors.lavender500};
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: 1.75px;
  text-align: right;
  text-transform: uppercase;

  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.lavender600};
  }
`;

export const ProductGridFilterDrawerButton = styled(BaseFilterDrawerButton)`
  font-size: 12px;
  text-transform: uppercase;
  color: ${props => props.theme.colors.black};

  span {
    color: ${props => props.theme.colors.black};
  }
`;

export const ProductGridFilterIcon = styled(BaseFilterIcon)`
  vertical-align: text-bottom;
  margin-right: 6px;
  width: ${({ theme }) => theme.spacing.small}px;
  ${({ isBottom }) =>
    isBottom
      ? css`
          margin: 2px 0 0 6px;
        `
      : css`
          margin-right: 6px;
        `}
`;

export const productGridFloatingFilterLabelStyle = css`
  margin: 0 5px 0 0;
`;

export const ProductGridFloatingFilterButton = styled(BaseFloatingFilterButton)`
  min-width: 88px;
  ${({ theme }) =>
    css`
      ${theme.button.variants.default.buttonStyle}
      ${theme.button.variants.primaryLavender.buttonStyle}
    `}
  ${({ theme }) => theme.paragraph.variants.paragraph4Uppercase.textStyles}
    min-height: unset;
  min-width: unset;
  line-height: 15px;
  border-radius: 0;
  color: black;
  ${({ $filterBarEnabled }) =>
    $filterBarEnabled &&
    css`
      margin-top: 70px;
    `};
  svg {
    margin-left: 4px;
  }
  text-transform: uppercase;

  & span {
    font-size: 12px;
  }

  ${({ isBottom }) =>
    isBottom &&
    css`
      height: 40px;
      padding: 12px ${props => props.theme.spacing.small}px 12px
        ${props => props.theme.spacing.small}px;
      margin: 0 0 ${props => props.theme.spacing.small}px 0;

      &[data-transition-state='exiting'],
      &[data-transition-state='exited'] {
        margin-bottom: 0px;
        transform: translate3d(0, 100%, 0);
      }

      > span > span {
        flex-direction: row-reverse;
        align-items: center;
        justify-content: center;
        letter-spacing: 1.75px;
      }
    `}
`;
const LabelText = styled.span`
  margin: 0 5px;
  ${({ isBottom }) =>
    isBottom &&
    css`
      order: 1;
    `}
`;

export const FilterBottomStickyContainer = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: center;
`;

export const ProductGridMobileFloatingFilterButton = ({
  filterBarEnabled,
  isBottom = false,
  buttonContainerRef = null,
}) => {
  const { isMobile } = useBreakpoint();
  const { colors } = useTheme();

  const icon = <ProductGridFilterIcon color="black" size={16} />;

  const drawerButtonProps = useMemo(
    () => ({
      beforeLabelIcon: null,
      afterLabelIcon: icon,
      filterCountStyle: productGridFilterDrawerBtnLabelStyleVariant,
      label: (
        <FormattedMessage
          id="site_product_grid.filter_and_sort_title"
          defaultMessage="Filter & Sort"
        />
      ),
      labelStyle: productGridFilterDrawerBtnLabelStyleVariant,
    }),
    [icon]
  );

  const filterButton = isMobile ? (
    <ProductGridFloatingFilterButton
      label={
        <LabelText isBottom={isBottom}>
          <FormattedMessage
            id="site_product_grid.filter_and_sort_title"
            defaultMessage="Filter & Sort"
          />
        </LabelText>
      }
      labelTextStyle={productGridFloatingFilterLabelStyle}
      icon={
        <ProductGridFilterIcon
          color={colors.black}
          size={14}
          isBottom={isBottom}
        />
      }
      $filterBarEnabled={filterBarEnabled}
      isBottom={isBottom}
    />
  ) : null;

  return (
    <BaseFloatingFilterButton.Provider>
      <BaseFloatingFilterButton.InlineButtonObserver>
        <ProductGridFilterDrawerButton {...drawerButtonProps} />
      </BaseFloatingFilterButton.InlineButtonObserver>
      {(buttonContainerRef?.current &&
        createPortal(filterButton, buttonContainerRef.current)) ||
        null}
    </BaseFloatingFilterButton.Provider>
  );
};

ProductGridMobileFloatingFilterButton.propTypes = {
  filterBarEnabled: PropTypes.bool,
  isBottom: PropTypes.bool,
  buttonContainerRef: PropTypes.oneOfType([
    PropTypes.shape({ current: PropTypes.instanceOf(PropTypes.Element) }),
  ]),
  showNewFilteringAndSortingUxInMobileFlag: PropTypes.bool,
};
